import { payResources } from "../resources/resource-pool";
import { getCardInstance, getCommanderDefinitionForInstance } from "../selectors/card-instances";
import { calculateEventLimit } from "../selectors/event-limit";
import type {
  GameEvent,
  GameStateV3,
  GameStateV4,
  GameStateV5,
  ReplaceCommanderAction,
} from "../types";
import type { TransitionResult } from "./advance-phase";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function applyReplaceCommander(
  state: Readonly<GameStateV3 | GameStateV4 | GameStateV5>,
  action: ReplaceCommanderAction,
  actionSequence: number,
): TransitionResult {
  const newInstance = getCardInstance(state, action.commanderCardInstanceId);
  const definition = getCommanderDefinitionForInstance(state, action.commanderCardInstanceId);
  const player = state.players[action.playerId];
  const lane = state.lanes[action.laneId];
  const side = lane.players[action.playerId];
  const oldCommander = side.commander;
  const oldInstance =
    oldCommander === null ? undefined : getCardInstance(state, oldCommander.cardInstanceId);
  if (
    newInstance === undefined ||
    definition === undefined ||
    oldCommander === null ||
    oldInstance === undefined
  ) {
    throw new Error("Validated Commander replacement lost its incoming or outgoing definition.");
  }

  const previousKingdomPool = { ...player.kingdomPool };
  const costPaid = { ...definition.cost };
  const remainingKingdomPool = payResources(previousKingdomPool, costPaid);
  const eventLimitBefore = calculateEventLimit(state, action.playerId);
  const sourceAlreadyUsed = player.actionsUsed.attacksMadeFromLanes.includes(action.laneId);
  const nextState: GameStateV3 | GameStateV4 | GameStateV5 = {
    ...state,
    players: {
      ...state.players,
      [action.playerId]: {
        ...player,
        hand: player.hand.filter(
          (cardInstanceId) => cardInstanceId !== action.commanderCardInstanceId,
        ),
        discard: [...player.discard, oldCommander.cardInstanceId],
        kingdomPool: remainingKingdomPool,
        actionsUsed: {
          ...player.actionsUsed,
          deploymentActions: 1,
        },
      },
    },
    lanes: {
      ...state.lanes,
      [action.laneId]: {
        ...lane,
        players: {
          ...lane.players,
          [action.playerId]: {
            ...side,
            commander: {
              cardInstanceId: action.commanderCardInstanceId,
              attack: definition.attack,
              guard: definition.guard,
              ...("armyCapacity" in definition ? { armyCapacity: definition.armyCapacity } : {}),
              readiness:
                (state.schemaVersion === 4 || state.schemaVersion === 5) && sourceAlreadyUsed
                  ? "spent"
                  : "ready",
              deployedThisTurn: true,
              mayAttackOnDeploymentTurn: state.schemaVersion === 4 || state.schemaVersion === 5,
            },
          },
        },
      },
    },
  };
  const garrisonArmyCardInstanceIds = side.armies.map((army) => army.cardInstanceId);
  const events: GameEvent[] = [
    {
      type: "kingdom-pool-spent",
      eventId: eventId(actionSequence, 0),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      commanderCardInstanceId: action.commanderCardInstanceId,
      cardDefinitionId: newInstance.cardDefinitionId,
      reason: "replace-commander",
      costPaid,
      previousKingdomPool,
      remainingKingdomPool,
    },
    {
      type: "commander-replaced",
      eventId: eventId(actionSequence, 1),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      oldCommanderCardInstanceId: oldCommander.cardInstanceId,
      oldCommanderDefinitionId: oldInstance.cardDefinitionId,
      newCommanderCardInstanceId: action.commanderCardInstanceId,
      newCommanderDefinitionId: newInstance.cardDefinitionId,
      oldCommanderDestination: "discard",
      printedAttack: definition.attack,
      printedGuard: definition.guard,
      ...("armyCapacity" in definition ? { armyCapacity: definition.armyCapacity } : {}),
      costPaid,
      remainingKingdomPool,
      garrisonArmyCardInstanceIds,
      eventLimitBefore,
      eventLimitAfter: calculateEventLimit(nextState, action.playerId),
      authorizationLaneAlreadyUsed: player.actionsUsed.eventAuthorizationLanesUsed.includes(
        action.laneId,
      ),
    },
    {
      type: "deployment-action-used",
      eventId: eventId(actionSequence, 2),
      actionSequence,
      playerId: action.playerId,
      actionType: "replace-commander",
      deploymentActionsUsed: 1,
    },
  ];

  return { state: nextState, events };
}
